/**
 * Result Pattern
 * Provides explicit success/failure handling without throwing exceptions
 */
import { ServiceError } from './service-error';

export class Result<T> {
  private constructor(
    public readonly isSuccess: boolean,
    public readonly error?: ServiceError,
    private readonly _value?: T,
  ) {}

  /**
   * Create a successful result
   */
  public static ok<U>(value: U): Result<U> {
    return new Result<U>(true, undefined, value);
  }

  /**
   * Create a failed result
   */
  public static fail<U>(error: ServiceError): Result<U> {
    return new Result<U>(false, error);
  }

  /**
   * Get the value (throws if result is failure)
   */
  public getValue(): T {
    if (!this.isSuccess) {
      throw new Error(
        `Cannot get value from failed result. Error: ${this.error?.message}`,
      );
    }
    return this._value as T;
  }

  /**
   * Get the error (throws if result is success)
   */
  public getError(): ServiceError {
    if (this.isSuccess) {
      throw new Error('Cannot get error from successful result');
    }
    return this.error as ServiceError;
  }

  /**
   * Check if result is failure
   */
  public get isFailure(): boolean {
    return !this.isSuccess;
  }

  /**
   * Map the value if success, otherwise pass through error
   */
  public map<U>(fn: (value: T) => U): Result<U> {
    if (this.isFailure) {
      return Result.fail<U>(this.error!);
    }
    try {
      return Result.ok(fn(this._value as T));
    } catch (error: unknown) {
      return Result.fail<U>(
        new ServiceError('MAPPING_ERROR', 'Error during result mapping', error),
      );
    }
  }

  /**
   * Flat map - useful for chaining operations that return Results
   */
  public flatMap<U>(fn: (value: T) => Result<U>): Result<U> {
    if (this.isFailure) {
      return Result.fail<U>(this.error!);
    }
    try {
      return fn(this._value as T);
    } catch (error: unknown) {
      return Result.fail<U>(
        new ServiceError('FLATMAP_ERROR', 'Error during result flatMap', error),
      );
    }
  }

  /**
   * Execute a function with the value if success
   */
  public tap(fn: (value: T) => void): Result<T> {
    if (this.isSuccess) {
      fn(this._value as T);
    }
    return this;
  }

  /**
   * Execute a function with the error if failure
   */
  public tapError(fn: (error: ServiceError) => void): Result<T> {
    if (this.isFailure) {
      fn(this.error!);
    }
    return this;
  }

  /**
   * Match pattern for handling success and failure cases
   */
  public match<U>(patterns: {
    ok: (value: T) => U;
    fail: (error: ServiceError) => U;
  }): U {
    if (this.isSuccess) {
      return patterns.ok(this._value as T);
    }
    return patterns.fail(this.error!);
  }

  /**
   * Convert to a Promise (useful for async/await)
   */
  public async toPromise(): Promise<T> {
    if (this.isSuccess) {
      return Promise.resolve(this._value as T);
    }
    return Promise.reject(this.error);
  }

  /**
   * Create Result from Promise
   */
  public static async fromPromise<U>(
    promise: Promise<U>,
    errorMessage = 'Promise rejected',
  ): Promise<Result<U>> {
    try {
      const value = await promise;
      return Result.ok(value);
    } catch (error: unknown) {
      return Result.fail(
        new ServiceError('PROMISE_ERROR', errorMessage, error),
      );
    }
  }

  /**
   * Combine multiple results - fails if any result fails
   */
  public static combine<U>(results: Result<U>[]): Result<U[]> {
    const failed = results.find((r) => r.isFailure);
    if (failed) {
      return Result.fail(failed.error!);
    }
    return Result.ok(results.map((r) => r.getValue()));
  }

  /**
   * Convert to plain object for serialization.
   *
   * REQUIRED at every server-action → client-component boundary: a `Result`
   * returned as the root of a server action reply is rejected by React's
   * Flight serializer ("Only plain objects ... can be passed to Client
   * Components"), because the root model bypasses the `toJSON` replacer that
   * rescues nested values. Declare the action's return type as
   * {@link PlainResult} and `.toJSON()` the value — never cast.
   *
   * NOTE: this is a SHALLOW conversion. `data` is passed through untouched,
   * so the wrapped value must itself already be plain.
   */
  public toJSON(): PlainResult<T> {
    if (this.isSuccess) {
      // Invariant (enforced by the private ctor): ok() always carries _value.
      return {
        success: true,
        data: this._value as T,
      };
    }
    // Invariant (enforced by the private ctor): fail() always carries error.
    return {
      success: false,
      error: this.error!.toJSON(),
    };
  }
}

/**
 * Plain-object form of Result, safe for Next.js server/client serialization.
 * Returned by `Result.toJSON()`.
 */
export type PlainResult<T> =
  | { success: true; data: T }
  | {
      success: false;
      error: {
        code: string;
        message: string;
        details?: Record<string, unknown>;
      };
    };

/**
 * Type guard to check if value is a Result
 */
export function isResult<T>(value: unknown): value is Result<T> {
  return Boolean(
    value &&
    typeof value === 'object' &&
    'isSuccess' in value &&
    'getValue' in value,
  );
}

/**
 * Utility to unwrap Result or throw error
 */
export function unwrap<T>(result: Result<T>): T {
  if (result.isSuccess) {
    return result.getValue();
  }
  throw result.getError();
}

/**
 * Utility to unwrap Result or return default value
 */
export function unwrapOr<T>(result: Result<T>, defaultValue: T): T {
  if (result.isSuccess) {
    return result.getValue();
  }
  return defaultValue;
}
